import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';

import CloseIcon from 'assets/icons/close.svg';
import { store } from 'store';
import {
  getMicAnalyser,
  getServerAnalyser,
  isConnected,
  isConnecting,
  startVoice,
  stopVoice,
} from 'store/assistant/voiceManager';

import * as S from './Assistant.styles';
import { SophieDebugPanel, TextChat } from './components';

const BUTTON_SHOW_DELAY = 1500;

export const Assistant: FC<{ showBug: boolean }> = observer(({ showBug }) => {
  const { setVoiceState, voiceState, assistantMode, setAssistantMode } = store.assistantStore;
  const { toggleMode } = store.commonStore;

  const [buttonVisible, setButtonVisible] = useState(false);

  const eqCanvasRef = useRef<HTMLCanvasElement>(null);
  const sophieCanvasRef = useRef<HTMLCanvasElement>(null);
  const eqAnimIdRef = useRef<number | null>(null);

  // ── Equalizer animation (reads from shared voiceManager analysers) ─────────
  const startEqualizer = useCallback(() => {
    const eqCanvas = eqCanvasRef.current;
    const sophieCanvas = sophieCanvasRef.current;
    if (!eqCanvas) return;

    const eqCtx = eqCanvas.getContext('2d');
    const sophieCtx = sophieCanvas ? sophieCanvas.getContext('2d') : null;
    if (!eqCtx) return;

    const barCount = 28;

    const drawFrame = () => {
      eqAnimIdRef.current = requestAnimationFrame(drawFrame);
      eqCtx.clearRect(0, 0, eqCanvas.width, eqCanvas.height);

      const micAn = getMicAnalyser();
      if (micAn) {
        const bufLen = micAn.frequencyBinCount;
        const micData = new Uint8Array(bufLen);
        micAn.getByteFrequencyData(micData);
        const barWidth = eqCanvas.width / barCount - 2;
        const maxH = eqCanvas.height - 4;

        for (let i = 0; i < barCount; i++) {
          const idx = Math.floor((i * bufLen * 0.4) / barCount);
          const val = micData[idx] / 255;
          const h = Math.max(2, val * maxH);
          const x = i * (barWidth + 2) + 1;
          const y = (eqCanvas.height - h) / 2;
          const r = Math.floor(60 * val);
          const g = Math.floor(180 + 75 * val);
          const b = Math.floor(100 + 36 * val);

          eqCtx.fillStyle = `rgba(${r},${g},${b},${0.6 + val * 0.4})`;
          eqCtx.beginPath();
          if (eqCtx.roundRect) {
            eqCtx.roundRect(x, y, barWidth, h, 1);
          } else {
            eqCtx.rect(x, y, barWidth, h);
          }
          eqCtx.fill();

          if (val > 0.3) {
            eqCtx.shadowColor = `rgba(0,255,136,${val * 0.4})`;
            eqCtx.shadowBlur = 3;
            eqCtx.fill();
            eqCtx.shadowBlur = 0;
          }
        }
      }

      const srvAn = getServerAnalyser();
      if (sophieCtx && sophieCanvas && srvAn) {
        const w = sophieCanvas.width;
        const h = sophieCanvas.height;
        const cx = w / 2;
        const cy = h / 2;
        const maxR = w * 0.45;
        const minR = w * 0.15;

        const srvData = new Uint8Array(srvAn.frequencyBinCount);
        srvAn.getByteFrequencyData(srvData);
        const rms = Math.sqrt(srvData.reduce((a, v) => a + v * v, 0) / srvData.length);
        const intensity = Math.min(1, (rms * 1.4) / 255);
        const radius = minR + intensity * (maxR - minR);

        sophieCtx.clearRect(0, 0, w, h);
        sophieCtx.beginPath();
        sophieCtx.arc(cx, cy, radius, 0, 2 * Math.PI);
        sophieCtx.fillStyle = `rgba(78,136,0,${0.5 + intensity * 0.5})`;
        sophieCtx.fill();

        sophieCtx.beginPath();
        sophieCtx.arc(cx, cy, minR, 0, 2 * Math.PI);
        sophieCtx.fillStyle = '#76B900';
        sophieCtx.fill();

        sophieCtx.beginPath();
        sophieCtx.arc(cx, cy, maxR, 0, 2 * Math.PI);
        sophieCtx.strokeStyle = 'rgba(0,255,136,0.3)';
        sophieCtx.lineWidth = 1;
        sophieCtx.stroke();
      } else if (sophieCtx && sophieCanvas) {
        const cx = sophieCanvas.width / 2;
        const cy = sophieCanvas.height / 2;
        sophieCtx.clearRect(0, 0, sophieCanvas.width, sophieCanvas.height);
        sophieCtx.beginPath();
        sophieCtx.arc(cx, cy, sophieCanvas.width * 0.15, 0, 2 * Math.PI);
        sophieCtx.fillStyle = '#76B900';
        sophieCtx.fill();
      }
    };

    drawFrame();
  }, []);

  const stopEqualizer = useCallback(() => {
    if (eqAnimIdRef.current) {
      cancelAnimationFrame(eqAnimIdRef.current);
      eqAnimIdRef.current = null;
    }
  }, []);

  // ── Auto-stop when assistantMode changes away from 'voice' ────────────────
  useEffect(() => {
    if (assistantMode !== 'voice' && isConnected()) {
      stopVoice();
    }
  }, [assistantMode]);

  // ── Toggle voice ──────────────────────────────────────────────────────────
  const toggleVoiceAssistant = useCallback(async () => {
    if (isConnecting()) return;
    if (isConnected()) {
      setAssistantMode(null);
      return;
    }

    setAssistantMode('voice');
    setVoiceState('connecting');
    await startVoice();
  }, [setAssistantMode, setVoiceState]);

  const toggleTextChat = useCallback(() => {
    setAssistantMode(assistantMode === 'text' ? null : 'text');
  }, [assistantMode, setAssistantMode]);

  // ── Equalizer lifecycle ───────────────────────────────────────────────────
  useEffect(() => {
    if (voiceState === 'active') startEqualizer();
    return () => stopEqualizer();
  }, [voiceState, startEqualizer, stopEqualizer]);

  useEffect(() => {
    const timer = setTimeout(() => setButtonVisible(true), BUTTON_SHOW_DELAY);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    return () => {
      stopVoice();
    };
  }, []);

  if (!buttonVisible) return null;

  const voiceButtonText = (() => {
    if (voiceState === 'connecting') return 'Connecting...';
    if (voiceState === 'active') return 'Listening...';
    if (voiceState === 'error') return 'Error';
    return "Let's Talk";
  })();

  const chatButtonText = assistantMode === 'text' ? 'Close Chat' : 'Text Chat';

  return (
    <S.Container>
      {voiceState === 'active' && (
        <S.Equalizer>
          <S.SophieCircle ref={sophieCanvasRef} width={60} height={60} />
          <S.EqCanvas ref={eqCanvasRef} width={280} height={60} />
          <S.CloseButton onClick={() => setAssistantMode(null)}>
            <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 4L4 12M4 4l8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </S.CloseButton>
        </S.Equalizer>
      )}
      {voiceState !== 'active' && (
        <S.ButtonWrapper>
          <S.VoiceButton $connecting={voiceState === 'connecting'} onClick={toggleVoiceAssistant}>
            {voiceButtonText}
          </S.VoiceButton>
          <S.TextButton $active={assistantMode === 'text'} onClick={toggleTextChat}>
            {chatButtonText}
          </S.TextButton>
          <S.CloseAssistantButton onClick={() => toggleMode('assistant')}>
            <CloseIcon />
          </S.CloseAssistantButton>
        </S.ButtonWrapper>
      )}
      <SophieDebugPanel showBug={showBug} />
      <TextChat />
    </S.Container>
  );
});
